import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormArray } from '@angular/forms';

import { catchError, filter, map, switchMap } from 'rxjs/operators';

import { Attachment, Reference } from '@library/common/models';
import { CountriesService, EnumsService } from '@library/core/services';
import { AttachmentService } from '@library/modules/attachment';

import { ShipmentDetailsService } from './shipment-details.service';
import { QuotationDetailsService } from '@library/modules/api-services';
import { forkJoin, Observable, of } from 'rxjs';
import { IGenericType } from '@library/common/generictype';

@Component({
   selector: 'lib-shipment-details',
   inputs: [ 'hawb' ],
   templateUrl: './details.component.html',
   styleUrls: [ './details.component.scss' ],
})
export class ShipmentDetailsComponent implements OnInit, OnChanges  {

   @Input()
   public mode = 'shipment';

   @Input()
   public hawb: string;

   @ViewChild('textContent') textContent!: ElementRef;

   shipment$: Observable<any>;

   // Built once per hawb/mode change in loadShipment(), not recomputed on every
   // change-detection cycle. This used to be a getter (`get allItems()`) called
   // directly from four `*ngIf="allItems | async as items"` template bindings,
   // which re-invoked `details.load()`/`quoteDetails.load()` — and therefore
   // fired a fresh HTTP request — on every CD tick, including ticks triggered
   // by something unrelated while the tab was otherwise idle. See ANG-1392.
   items$: Observable<any>;

   // Built once per hawb/mode change in loadShipment(), mirroring items$ above.
   // Only populated in 'quote' mode; the template used to call
   // `quoteDetails.load( hawb ) | async` directly, which re-invoked the service
   // — and fired a fresh HTTP request — on every CD cycle, the same anti-pattern
   // items$ was introduced to fix. See ANG-1392.
   quotation$: Observable<any>;

   allowNewAttachments = false;

   attachmentUpdates: FormArray = new FormArray( [] );

   expanded: boolean = false;
   showToggle: boolean = false;
   remainingAccessorials: IGenericType;

   quoteStatus: IGenericType;

   constructor(
      public countries: CountriesService,
      public enums: EnumsService,
      public details: ShipmentDetailsService,
      public quoteDetails: QuotationDetailsService,

      private attachmentService: AttachmentService,
      private route: ActivatedRoute,
   ) { }

   ngOnInit() {
      this.route.data.pipe( filter( data => data?.allowNewAttachments ))
         .subscribe( data => this.allowNewAttachments = data?.allowNewAttachments );
      this.loadShipment();
   }

   ngOnChanges(changes: SimpleChanges) {
      if (changes['hawb'] && !changes['hawb'].firstChange) {
         this.loadShipment();
      }
   }

   private loadShipment() {
      if (this.hawb) {
         const itemsSource$: Observable<any> = this.mode === 'shipment' ?
            this.details.load( this.hawb ) : this.quoteDetails.load( this.hawb );

         this.items$ = itemsSource$.pipe(
            map( s => s?.calculator ?? ( s['containers'] ?? [] )
               .filter( c => c?.items?.length )
               .reduce(( a, c ) => [ ...a, ...c?.items ], [] )),
         );

         // Mirrors items$: computed once here instead of the template calling
         // quoteDetails.load( hawb ) directly. Only relevant in 'quote' mode.
         this.quotation$ = this.mode === 'quote' ? this.quoteDetails.load( this.hawb ) : of( null );

         this.shipment$ = this.details.load(this.hawb).pipe(
            switchMap(shipment => {
               if (!shipment) {
                  return of(shipment);
               }
               
               // Pre-compute all container numbers and seal numbers for the shipment
               const containers = (shipment as any).containers;
               (shipment as any).allContainerNumbers = this.getPropertyFromContainers(containers, 'number');
               (shipment as any).allSealNumbers = this.getPropertyFromContainers(containers, 'sealNumber');
               
               // Load freight booking details if they exist
               if (shipment.freightBookings?.length) {
                  const fbCalls = shipment.freightBookings.map(fb => 
                     this.details.viewFreightBooking(fb).pipe(
                        catchError(() => of(null))
                     )
                  );
                  
                  return forkJoin(fbCalls).pipe(
                     map(fbDetails => {
                        // Filter out null values from errors and merge into shipment
                        const filteredFbDetails = fbDetails.filter(fb => fb !== null);
                        
                        // Pre-compute container numbers and seal numbers for each freight booking
                        filteredFbDetails.forEach((fb: any) => {
                           // Only show containers/seals that belong to this specific freight booking
                           fb.computedContainerNumbers = this.getPropertyFromContainers(containers, 'number', fb.containers);
                           fb.computedSealNumbers = this.getPropertyFromContainers(containers, 'sealNumber', fb.containers);
                        });
                        
                        (shipment as any).freightBookingDetails = filteredFbDetails;
                        return shipment;
                     }),
                     catchError(() => {
                        // If all calls fail, return shipment with empty array
                        (shipment as any).freightBookingDetails = [];
                        return of(shipment);
                     })
                  );
               }
               
               return of(shipment);
            })
         );
      } else {
         this.shipment$ = of(null);
         this.items$ = of(null);
         this.quotation$ = of(null);
      }
   }

   getBillingPartyRef( shipment: IGenericType ): Reference {
      return shipment?.references?.filter( r => r.party === (shipment?.billingparty ?? shipment?.billingParty) )[0];
   }

   getSortedStatusesById(statuses: any[]): any[] {
      if (!statuses) return [];

      return [...statuses].sort((a, b) => a.orderingId - b.orderingId);
   }

   isShipmentDelayed( shipment: IGenericType ): boolean {
      const statuses = this.getSortedStatusesById( shipment?.statuses );
      return statuses[0]?.event === 'Delay';
   }

   getDelayReason( shipment: IGenericType ): string {
      const statuses = this.getSortedStatusesById( shipment?.statuses );
      return statuses[0]?.reason || '';
   }

   saveAttachments( hawb: string, attachments: Attachment[] ): void {
      this.attachmentService.savingState = 'saving';

      forkJoin( attachments.map(( attch ) => this.attachmentService.uploadAttachment({ hawb, attachments: [attch] })))
         .subscribe( _ => {
            this.attachmentService.savingState = 'show';

            this.attachmentUpdates.clear();
            this.details.reset();
            // reset() only clears the cached shipment; without re-running
            // loadShipment() the view keeps showing stale data until the next
            // unrelated hawb/mode change. See ANG-1392 review follow-up.
            this.loadShipment();
         });
   }

   saveQuoteAttachments( quoteNumber: string, attachments: Attachment[] ): void {
      this.attachmentService.savingState = 'saving';

      forkJoin( attachments.map(( attch ) => this.attachmentService.uploadAttachment({ quoteNumber, attachments: [attch] })))
         .subscribe( _ => {
            this.attachmentService.savingState = 'show';
            this.attachmentUpdates.clear();
            this.quoteDetails.reset();
            // Same reasoning as saveAttachments() above — refresh the view after
            // the cache is reset so the newly-uploaded attachment actually shows.
            this.loadShipment();
         });
   }

   getAccessorial(accessorials: IGenericType, expand = false){
      accessorials.sort(( a, b ) => {
         const descA = a.description.toLowerCase();
         const descB = b.description.toLowerCase();
         return descA < descB ? -1 : descA > descB ? 1 : 0;
      });
      const accessorialsLimit = 8;
      this.showToggle = accessorials.length > accessorialsLimit;
      this.remainingAccessorials = accessorials.length - accessorialsLimit;

      const filteredAccessorials = accessorials
         .filter((_,i)=> expand || i < accessorialsLimit)
         .map(x=> x.description ).join(', ');

      return filteredAccessorials;
   }

   private getPropertyFromContainers(shipmentContainers: any[], propertyName: string, fbContainers?: any[]): string {
      if (!shipmentContainers || !Array.isArray(shipmentContainers)) return '';
      
      // If fbContainers provided, filter to only matching containers
      if (fbContainers && Array.isArray(fbContainers)) {
         // Get all container numbers from freight booking (including empty strings)
         const fbContainerNumbers = fbContainers.map(fbc => fbc.containerNumber);
         const values = shipmentContainers
            .filter(container => fbContainerNumbers.includes(container.number))
            .map(container => container[propertyName])
            .filter(value => value);
         return values.length > 0 ? values.join(', ') : '';
      }
      
      // Fallback: return all values
      const values = shipmentContainers
         .map(container => container[propertyName])
         .filter(value => value);
      
      return values.length > 0 ? values.join(', ') : '';
   }
}
